import type {
  GetInvoiceById,
  GetStripeLoginLink,
  Invoice,
} from '@/shared/domain'
import { formatCurrency, formatDate } from '@/shared/infra/format'
import { TableCell, TableRow } from '@/shared/ui/primitives'

import { InvoiceDisputeBadge } from './invoice-dispute-badge'
import { InvoiceStatusBadge } from './invoice-status-badge'
import { INVOICE_TYPE_LABELS, PAYMENT_METHOD_LABELS } from './payment-labels'
import { PaymentDetailPanel } from './payment-detail-panel'
import { PaymentRowActions } from './payment-row-actions'

export type PaymentRowActionHandlers = {
  orgId: string
  onGetInvoiceById: GetInvoiceById
  onGetStripeLoginLink: GetStripeLoginLink
  sendingId: string | null
  resendingId: string | null
  downloadingId: string | null
  viewingReceiptId: string | null
  editingId: string | null
  markingAsPaidId: string | null
  onSend: (invoice: Invoice) => void
  onResend: (invoice: Invoice) => void
  onDownloadPdf: (invoice: Invoice) => void
  onViewReceipt: (invoice: Invoice) => void
  onRequestVoid: (invoice: Invoice) => void
  onRequestRefund: (invoice: Invoice) => void
  onRequestEdit: (invoice: Invoice) => void
  onRequestExtend: (invoice: Invoice) => void
  onMarkAsPaid: (invoice: Invoice) => void
}

export type PaymentRowProps = {
  invoice: Invoice
  isExpanded: boolean
  onToggleExpand: (id: string) => void
  actions: PaymentRowActionHandlers
  isHighlighted?: boolean
}

export function PaymentRow({
  invoice,
  isExpanded,
  onToggleExpand,
  actions,
  isHighlighted,
}: PaymentRowProps) {
  const handleToggle = () => onToggleExpand(invoice.id)

  return (
    <>
      <TableRow
        data-row-id={invoice.id}
        className={`cursor-pointer ${isHighlighted ? 'bg-accent/10 ring-2 ring-accent' : ''}`}
        onClick={handleToggle}
      >
        <TableCell>
          {formatDate(invoice.createdAt)}
        </TableCell>
        <TableCell>
          {invoice.clientName ?? invoice.recipientEmail ?? '—'}
        </TableCell>
        <TableCell>{INVOICE_TYPE_LABELS[invoice.invoiceType]}</TableCell>
        <TableCell>{invoice.paymentMethod ? PAYMENT_METHOD_LABELS[invoice.paymentMethod] : '—'}</TableCell>
        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
        <TableCell>
          {invoice.status === 'scheduled'
            ? invoice.scheduledSendDate
              ? `Delivers ${formatDate(invoice.scheduledSendDate)}`
              : '—'
            : invoice.dueDate
              ? formatDate(invoice.dueDate)
              : '—'}
        </TableCell>
        <TableCell>
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <InvoiceStatusBadge
              status={invoice.status}
              emailDeliveryStatus={invoice.emailDeliveryStatus}
            />
            {invoice.dispute ? (
              <InvoiceDisputeBadge dispute={invoice.dispute} />
            ) : null}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <PaymentRowActions
            invoice={invoice}
            isExpanded={isExpanded}
            sendingId={actions.sendingId}
            resendingId={actions.resendingId}
            downloadingId={actions.downloadingId}
            viewingReceiptId={actions.viewingReceiptId}
            editingId={actions.editingId}
            markingAsPaidId={actions.markingAsPaidId}
            onSend={actions.onSend}
            onResend={actions.onResend}
            onDownloadPdf={actions.onDownloadPdf}
            onViewReceipt={actions.onViewReceipt}
            onRequestVoid={actions.onRequestVoid}
            onRequestRefund={actions.onRequestRefund}
            onRequestEdit={actions.onRequestEdit}
            onRequestExtend={actions.onRequestExtend}
            onMarkAsPaid={actions.onMarkAsPaid}
            onToggleExpand={onToggleExpand}
          />
        </TableCell>
      </TableRow>
      {isExpanded ? (
        <TableRow data-row-id={`${invoice.id}-detail`}>
          <TableCell colSpan={8} className="p-4">
            <PaymentDetailPanel
              invoice={invoice}
              orgId={actions.orgId}
              onGetInvoiceById={actions.onGetInvoiceById}
              onGetStripeLoginLink={actions.onGetStripeLoginLink}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}
